import React from 'react'
import { inject, observer } from 'mobx-react'
import { observable } from 'mobx'
import { Tooltip } from 'react-leaflet'
import IconMarker from './IconMarker'
import PoiTooltip from '../common/PoiTooltip'

const MarkerLayer = ({
  marker,
  uiStore,
  dataStore,
  onMouseDown,
  onClick,
  className = '',
  draggable = false,
  onDragEnd
}) => {
  if (!marker || !marker.position || !marker.position.latLng) return null

  // Resolve display data for tooltip (name, address, type, icon, bucket tags)
  // Markers coming from Google Places have address/type/icon_url directly.
  // Saved markers loaded from the backend may not, so we try to fall back
  // to a matching POI temporary marker (same lat/lng) to restore these fields.
  // Prefer explicit address, but fall back to description to support older markers.
  let displayAddress = marker.address || marker.description
  let displayType = marker.type || (marker.types && typeof marker.types === 'string' ? marker.types : '')
  let displayIconUrl = marker.icon_url

  if (dataStore && dataStore.poi) {
    const poi = dataStore.poi
    const EPS = 1e-4
    const mLat = marker.position.latLng.lat
    const mLng = marker.position.latLng.lng

    // Prefer matching by id (saved marker ↔ POI result), then fall back to lat/lng
    const matchById = poi.temporaryMarkers.find(m => m.id === marker.id)
    const matchByPosition = poi.temporaryMarkers.find(m => {
      const pos = m.position && m.position.latLng
      if (!pos) return false
      return Math.abs(pos.lat - mLat) < EPS && Math.abs(pos.lng - mLng) < EPS
    })

    const match = matchById || matchByPosition

    if (match) {
      if (!displayAddress && match.address) displayAddress = match.address
      if (!displayType) {
        displayType = match.type || (match.types && typeof match.types === 'string' ? match.types : '')
      }
      if (!displayIconUrl && match.icon_url) displayIconUrl = match.icon_url
    }
  }

  // Determine bucket-based color for the drop background:
  let bucketColor = '#ffffff'
  if (marker.is_competitor) {
    bucketColor = '#FAD165'
  } else if (marker.is_ally) {
    bucketColor = '#5EB1BC'
  } else if (marker.is_general) {
    bucketColor = '#0a2c4f'
  }

  const handleMouseOver = () => {
    if (uiStore && typeof uiStore.setHoveredMarkerForZIndex === 'function') {
      uiStore.setHoveredMarkerForZIndex(marker.id)
    }
    // Removed ensureMarkerDetails call on hover to prevent unnecessary API calls
    // if (dataStore && dataStore.poi && typeof dataStore.poi.ensureMarkerDetails === 'function') {
    //   dataStore.poi.ensureMarkerDetails(marker)
    // }
    // For pure hover behavior: if no marker is pinned by click,
    // hovering the marker shows its tooltip.
    if (uiStore && typeof uiStore.setHoveredMarker === 'function') {
      if (!uiStore.clickedMarkerId) {
        uiStore.setHoveredMarker(marker.id)
      }
    }
  }

  const handleMouseOut = () => {
    if (uiStore && typeof uiStore.clearHoveredMarkerForZIndex === 'function') {
      uiStore.clearHoveredMarkerForZIndex(marker.id)
    }
    // When leaving the marker:
    // - If there is no pinned marker, hide the tooltip.
    // - If there is a pinned marker (clickedMarkerId), leave tooltip state alone;
    //   it is controlled by click, not hover.
    if (uiStore && typeof uiStore.clearHoveredMarker === 'function') {
      if (!uiStore.clickedMarkerId) {
        uiStore.clearHoveredMarker()
      }
    }
  }

  const handleClick = (e) => {
    // Call the passed onClick handler (e.g., from SavedMarkerLayer to select the marker)
    if (onClick) {
      onClick(e)
    }
    // Show tooltip on click
    if (uiStore && typeof uiStore.setHoveredMarker === 'function') {
      uiStore.setHoveredMarker(marker.id)
    }
    if (uiStore && typeof uiStore.setClickedMarker === 'function') {
      uiStore.setClickedMarker(marker.id)
    }
  }

  const handleViewMore = () => {
    if (!uiStore || !dataStore || !dataStore.poi) return
    // Open POI menu
    uiStore.isPOIMenuExpanded = true
    // Determine if this is a saved marker
    const savedMarkerId = marker.savedMarkerId || (typeof marker.id === 'number' ? marker.id : null)
    const markerId = savedMarkerId ? `saved_${savedMarkerId}` : marker.id
    // Set marker in temporaryMarkers
    // Preserve color_id if marker has it, otherwise use getColor if it's a function
    // Priority: marker.color_id > marker.getColor() > marker.getColor (number) > 1
    let markerColorId = marker.color_id
    if (markerColorId == null || markerColorId === undefined) {
      if (typeof marker.getColor === 'function') {
        const colorObj = marker.getColor()
        markerColorId = colorObj ? colorObj.id : 1
      } else if (typeof marker.getColor === 'number') {
        markerColorId = marker.getColor
      } else {
        markerColorId = 1
      }
    }
    const singleMarker = {
      id: markerId,
      position: marker.position,
      name: marker.name || '',
      address: displayAddress || '',
      type: displayType || '',
      types: marker.types || '',
      getColor: markerColorId,
      color_id: markerColorId,
      icon_url: displayIconUrl,
      is_general: !!marker.is_general,
      is_ally: !!marker.is_ally,
      is_competitor: !!marker.is_competitor,
      savedMarkerId
    }
    dataStore.poi.temporaryMarkers = [singleMarker]
    dataStore.poi.placeResults = []
    dataStore.poi.hasBucketChanges = false
    // Clear and sync bucket information so marker appears in bucket cards
    dataStore.poi.bucketById.clear()
    dataStore.poi.sharedBucketsById.clear()
    dataStore.poi.general = []
    dataStore.poi.allies = []
    dataStore.poi.competitors = []
    if (singleMarker.is_general || singleMarker.is_ally || singleMarker.is_competitor) {
      const buckets = []
      if (singleMarker.is_general) buckets.push('general')
      if (singleMarker.is_ally) buckets.push('ally')
      if (singleMarker.is_competitor) buckets.push('competitor')
      if (buckets.length > 0) {
        dataStore.poi.sharedBucketsById.set(singleMarker.id, observable.array(buckets.slice()))
        dataStore.poi.bucketById.set(singleMarker.id, observable.array(buckets.slice()))
        dataStore.poi.disabledRowsById.set(singleMarker.id, true)
        if (singleMarker.is_general) dataStore.poi.general.push(singleMarker)
        if (singleMarker.is_ally) dataStore.poi.allies.push(singleMarker)
        if (singleMarker.is_competitor) dataStore.poi.competitors.push(singleMarker)
      }
    }
  }

  // Bring marker to front by setting high z-index:
  // Normalize marker ID for comparison (handle numeric IDs, string IDs, and "saved_123" format)
  const normalizeMarkerId = (id) => {
    if (id == null) return null
    const idStr = String(id)
    // If it's "saved_123", extract the numeric part
    if (idStr.startsWith('saved_')) {
      const numId = parseInt(idStr.replace('saved_', ''), 10)
      return isNaN(numId) ? id : numId
    }
    // Try to convert to number for consistent comparison
    const numId = Number(id)
    return isNaN(numId) ? id : numId
  }
  const normalizedMarkerId = normalizeMarkerId(marker.id)
  const normalizedSavedMarkerId = marker.savedMarkerId != null ? Number(marker.savedMarkerId) : null
  // Check hovered for z-index (handle both marker.id and savedMarkerId)
  const isHoveredForZIndex = uiStore && uiStore.hoveredMarkerIdsForZIndex && (
    uiStore.hoveredMarkerIdsForZIndex.has(marker.id) ||
    (normalizedSavedMarkerId != null && uiStore.hoveredMarkerIdsForZIndex.has(normalizedSavedMarkerId)) ||
    (normalizedMarkerId != null && uiStore.hoveredMarkerIdsForZIndex.has(normalizedMarkerId))
  )
  // Check tooltip visibility (handle both marker.id and savedMarkerId)
  const normalizedHoveredId = uiStore && uiStore.hoveredMarkerId != null ? normalizeMarkerId(uiStore.hoveredMarkerId) : null
  const showTooltip = normalizedHoveredId != null && (
    normalizedHoveredId === normalizedMarkerId ||
    normalizedHoveredId === normalizedSavedMarkerId
  )
  // A marker should be on top when:
  // - it is hovered (map or POI list),
  // - its tooltip is visible, or
  // - it is the "active" marker selected from the left/right menus (clickedMarkerId).
  const normalizedClickedId = uiStore && uiStore.clickedMarkerId != null ? normalizeMarkerId(uiStore.clickedMarkerId) : null
  const isActiveByClick = normalizedClickedId != null && (
    normalizedClickedId === normalizedMarkerId ||
    normalizedClickedId === normalizedSavedMarkerId
  )
  const isOnTop = isHoveredForZIndex || showTooltip || isActiveByClick
  const zIndexOffset = isOnTop ? 1000 : undefined
  // Separate flag: only set when hovering POI rows/mini-rows, not when hovering the marker itself
  const isPoiHoveredForAnimation = uiStore && uiStore.hoveredPoiMarkerIdsForAnimation && uiStore.hoveredPoiMarkerIdsForAnimation.has(marker.id)

  // Get color from color-menu for icon background
  // if color_id is 1 (default black), treat it as if no color-menu color was set
  let selectedColorForIcon = null
  if (marker && marker.color_id != null && marker.color_id !== 1 && dataStore && dataStore.markerColors) {
    if (typeof marker.getColor === 'function') {
      const colorObj = marker.getColor()
      if (colorObj && colorObj.active_color) {
        selectedColorForIcon = colorObj.active_color
      }
    } else {
      // POI temporary marker or other marker without getColor function
      // Look up color_id directly in markerColors
      const colorItem = dataStore.markerColors.find(c => c.id === marker.color_id)
      if (colorItem && colorItem.active_color) {
        selectedColorForIcon = colorItem.active_color
      }
    }
  }

  const handleContextMenu = (e) => {
    if (e && e.originalEvent) {
      e.originalEvent.preventDefault()
      e.originalEvent.stopPropagation()
    }
    if (uiStore && typeof uiStore.openQuickCreate === 'function') {
      const pos = (marker.position && marker.position.latLng) || (e && e.latlng)
      uiStore.openQuickCreate(pos, marker)
    }
  }

  return (
    <IconMarker
      color={marker.getColor}
      iconUrl={displayIconUrl}
      bucketColor={bucketColor}
      isGeneral={!!marker.is_general}
      isAlly={!!marker.is_ally}
      isCompetitor={!!marker.is_competitor}
      selectedColorForIcon={selectedColorForIcon}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      onContextMenu={handleContextMenu}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      position={marker.position.latLng}
      icon='custom-map-marker'
      selected={className.indexOf('selected') > -1}
      className={`custom-map-marker ${className} ${isPoiHoveredForAnimation ? 'custom-map-marker--poi-hovered' : ''} ${showTooltip ? 'hidden-name' : ''}`}
      draggable={draggable}
      onDragEnd={onDragEnd}
      label={uiStore && uiStore.showMarkerLabels ? marker.name : null}
      zIndexOffset={zIndexOffset}
    >
      {showTooltip && (
        <Tooltip direction='top' offset={[0, -30]} permanent sticky={false}>
          <PoiTooltip
            isRightPaneVisible={uiStore.isPOIMenuExpanded}
            name={marker.name}
            address={displayAddress}
            type={displayType}
            iconUrl={displayIconUrl}
            isGeneral={!!marker.is_general}
            isAlly={!!marker.is_ally}
            isCompetitor={!!marker.is_competitor}
            variant='marker'
            onViewMore={handleViewMore}
            onMouseEnterTooltip={() => {
              if (!uiStore || !marker || marker.id == null) return
              // Keep tooltip visible while hovering over it
              if (typeof uiStore.setHoveredMarker === 'function') {
                uiStore.setHoveredMarker(marker.id)
              }
            }}
            onMouseLeaveTooltip={() => {
              if (!uiStore || !marker || marker.id == null) return
              const clickedId = uiStore.clickedMarkerId
              if (typeof uiStore.setHoveredMarker === 'function' && typeof uiStore.clearHoveredMarker === 'function') {
                if (clickedId && clickedId === marker.id) {
                  // Tooltip pinned by click → keep it visible
                  uiStore.setHoveredMarker(clickedId)
                } else if (clickedId && clickedId !== marker.id) {
                  // Another marker is pinned → restore that tooltip
                  uiStore.setHoveredMarker(clickedId)
                } else {
                  // No pinned tooltip → hide on leaving tooltip
                  uiStore.clearHoveredMarker()
                }
              }
            }}
          />
        </Tooltip>
      )}
    </IconMarker>
  )
}

export default inject('uiStore', 'dataStore')(
  observer(MarkerLayer)
)
